import { addEvent } from '@/modules/pmt/fetchers/affiliate.fetchers';
import type {
  AffiliateEvent,
  AffiliateSession,
  AffiliateTrackingConfig,
  BatchSubmissionPayload,
  RetryContext,
  TrackEventParams,
  TrackingServiceOptions,
} from '@/modules/pmt/interfaces';

import { calculateBackoffDelay } from '@/utils/api.utils';
import { type StorageSession, clearStoredSession, getStoredSession, storeSession } from '@/utils/localStorage.utils';
// Import utility functions
import { generateGA4StyleSessionId, generateId } from '@/utils/tracking.utils';

// Constants
const BATCH_SIZE = 10;

export class AffiliateTrackingService {
  private eventQueue: AffiliateEvent[] = [];

  private batchTimer: number | null = null;
  private currentSession: AffiliateSession | null = null;
  private retryQueue = new Map<string, RetryContext>();
  private config: AffiliateTrackingConfig;

  // GA4-aligned session management
  private readonly SESSION_STORAGE_KEY = 'pmc_ga4_session';

  private onEventTracked?: (event: AffiliateEvent) => void;
  private onBatchSubmitted?: (batch: BatchSubmissionPayload) => void;
  private onError?: (error: Error, context?: RetryContext) => void;

  constructor(options: TrackingServiceOptions = {}) {
    this.config = {
      batchInterval: 5000, // 5 seconds
      sessionTimeout: 30 * 60 * 1000, // 30 minutes (GA4 default)
      maxRetries: 3,
      baseRetryDelay: 1000, // 1 second
      maxRetryDelay: 30000, // 30 seconds
      ...options.config,
    };

    this.onEventTracked = options.onEventTracked;
    this.onBatchSubmitted = options.onBatchSubmitted;
    this.onError = options.onError;

    this.startBatchProcessor();
  }

  trackImpression({ productId, url, locale, mainCategory }: Omit<TrackEventParams, 'eventType'>): void {
    this.trackEvent({ eventType: 'impression', productId, locale, url, mainCategory });
  }

  trackClick({ productId, url, locale, mainCategory }: Omit<TrackEventParams, 'eventType'>): void {
    this.trackEvent({ eventType: 'click', productId, locale, url, mainCategory });
  }

  private trackEvent({ eventType, url, productId, locale, mainCategory }: TrackEventParams): void {
    try {
      const event: AffiliateEvent = {
        event_type: eventType,
        platform: 'web',
        url,
        product_id: productId,
        locale: locale || 'US',
        main_category: mainCategory,
        timestamp: new Date().toISOString(),
      };

      this.addEventToQueue(event);
      this.onEventTracked?.(event);
    } catch (error) {
      const trackingError = new Error(
        `Failed to track ${eventType}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
      this.onError?.(trackingError);
    }
  }

  getOrCreateSession(userId?: string): AffiliateSession {
    const now = Date.now();

    // Try to get existing session from browser storage (GA4-style persistence)
    const existingSession = getStoredSession(this.SESSION_STORAGE_KEY) as AffiliateSession | null;
    if (existingSession && this.isSessionValid(existingSession, now)) {
      existingSession.lastActivityTime = now;
      this.currentSession = existingSession;
      storeSession(this.SESSION_STORAGE_KEY, existingSession as StorageSession);
      return existingSession;
    }

    if (existingSession) {
      clearStoredSession(this.SESSION_STORAGE_KEY);
    }

    this.currentSession = {
      sessionId: generateGA4StyleSessionId(now),
      userId,
      startTime: now,
      lastActivityTime: now,
      isActive: true,
    };

    // Store session in browser storage for GA4-style persistence
    storeSession(this.SESSION_STORAGE_KEY, this.currentSession as StorageSession);

    return this.currentSession;
  }

  // Force start new session (useful for GA4-style session management)
  startNewSession(userId?: string): AffiliateSession {
    this.invalidateSession();
    return this.getOrCreateSession(userId);
  }

  invalidateSession(): void {
    if (this.currentSession) {
      this.currentSession.isActive = false;
      this.currentSession = null;
    }

    // Clear stored session (GA4-style cleanup)
    clearStoredSession(this.SESSION_STORAGE_KEY);
  }

  async flushEvents(): Promise<void> {
    return new Promise((resolve) => {
      if (this.eventQueue.length === 0) {
        resolve();
        return;
      }

      this.sendBatch()
        .then(() => resolve())
        .catch(() => resolve()); // Don't reject, just log the error
    });
  }

  destroy(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    this.eventQueue = [];
    this.retryQueue.clear();
    this.invalidateSession();
  }

  getQueueSize(): number {
    return this.eventQueue.length;
  }

  getRetryQueueSize(): number {
    return this.retryQueue.size;
  }

  private addEventToQueue(event: AffiliateEvent): void {
    this.eventQueue.push(event);
  }

  private startBatchProcessor(): void {
    this.batchTimer = setInterval(() => {
      if (this.eventQueue.length > 0) {
        this.sendBatch().catch(() => {
          // Errors are already handled in sendBatch method
        });
      }

      this.processRetryQueue();
    }, this.config.batchInterval) as unknown as number;
  }

  private async sendBatch(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    // Only take up to BATCH_SIZE events for this batch
    const events = this.eventQueue.splice(0, BATCH_SIZE);
    const session = this.getOrCreateSession();
    const batchId = generateId();

    const payload = {
      events,
    };

    try {
      const response = await addEvent(payload);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.onBatchSubmitted?.({ events, sessionInfo: session, batchId, timestamp: Date.now() });
    } catch (error) {
      const submissionError = new Error(
        `Failed to submit batch: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );

      this.addToRetryQueue({ events, sessionInfo: session, batchId, timestamp: Date.now() }, submissionError);
      this.onError?.(submissionError);
    }
  }

  private addToRetryQueue(payload: BatchSubmissionPayload, error: Error): void {
    const retryContext: RetryContext = {
      attempt: 0,
      lastError: error,
      payload,
    };

    this.retryQueue.set(payload.batchId, retryContext);
  }

  private processRetryQueue(): void {
    const now = Date.now();

    for (const [batchId, context] of Array.from(this.retryQueue.entries())) {
      if (context.nextRetryTime && now < context.nextRetryTime) {
        continue;
      }

      if (context.attempt >= this.config.maxRetries) {
        this.retryQueue.delete(batchId);
        const finalError = new Error(`Max retries exceeded for batch ${batchId}`);
        this.onError?.(finalError, context);
        continue;
      }

      context.attempt++;
      context.nextRetryTime =
        now + calculateBackoffDelay(context.attempt, this.config.baseRetryDelay, this.config.maxRetryDelay);

      this.retryBatch(context, batchId);
    }
  }

  private async retryBatch(context: RetryContext, batchId: string): Promise<void> {
    try {
      const retryPayload = {
        events: context.payload.events,
      };

      const response = await addEvent(retryPayload, context.attempt);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.retryQueue.delete(batchId);
      this.onBatchSubmitted?.(context.payload);
    } catch (error) {
      context.lastError = error instanceof Error ? error : new Error('Unknown retry error');
    }
  }

  private isSessionValid(session: AffiliateSession, currentTime: number): boolean {
    return session.isActive && currentTime - session.lastActivityTime < this.config.sessionTimeout;
  }
}
